import React, { useEffect, useState } from 'react';
import api from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import Modal from '../components/ui/Modal';
import EmptyState from '../components/ui/EmptyState';
import LoadingScreen from '../components/ui/LoadingScreen';
import { IconPlus } from '../components/ui/Icons';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { getTenantId } from '../utils/ids';
import { emptyTenantForm, formToTenantPayload, tenantToForm } from '../utils/tenantForm';
import { BrandFields } from '../components/BrandFields';

function TenantFormFields({ formData, setFormData, editingTenant, partners, kindLocked, hidePartnerSelect }) {
  const setField = (key, value) => setFormData((prev) => ({ ...prev, [key]: value }));

  return (
    <>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Name <span className="required">*</span></label>
          <input
            type="text"
            className="form-input"
            value={formData.name}
            onChange={(e) => setField('name', e.target.value)}
            required
            placeholder="Workspace or partner name"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Kind</label>
          <select
            className="form-input"
            value={formData.kind}
            disabled={Boolean(kindLocked || editingTenant)}
            onChange={(e) => setField('kind', e.target.value)}
          >
            <option value="customer">Customer</option>
            <option value="partner">Partner</option>
          </select>
        </div>
      </div>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Domain</label>
          <input
            type="text"
            className="form-input"
            value={formData.domain}
            onChange={(e) => setField('domain', e.target.value)}
            placeholder="app.acme.com"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Subdomain</label>
          <input
            type="text"
            className="form-input"
            value={formData.subdomain}
            onChange={(e) => setField('subdomain', e.target.value.toLowerCase())}
            placeholder="acme"
            disabled={!!editingTenant}
          />
          <span className="form-hint">Leave empty to auto-generate from name. Reserved labels like partner cannot be used.</span>
        </div>
      </div>
      {formData.kind === 'customer' && !hidePartnerSelect && (
        <div className="form-group">
          <label className="form-label">Owned by partner</label>
          <select
            className="form-input"
            value={formData.partnerId}
            onChange={(e) => setField('partnerId', e.target.value)}
          >
            <option value="">Direct (no partner)</option>
            {partners.map((partner) => (
              <option key={getTenantId(partner)} value={getTenantId(partner)}>
                {partner.name} ({partner.subdomain})
              </option>
            ))}
          </select>
        </div>
      )}
      <BrandFields
        formData={formData}
        setField={setField}
        hint="Shown on this workspace’s domain. Empty fields inherit partner branding, then Inaiy blue."
      />
      <h3 className="form-section-title">Billing</h3>
      {formData.kind === 'partner' ? (
        <>
          <label className="form-check">
            <input
              type="checkbox"
              checked={formData.platformBillsPartner}
              onChange={(e) => setField('platformBillsPartner', e.target.checked)}
            />
            Platform bills this partner (wholesale)
          </label>
          <div className="form-group">
            <label className="form-label">Default retail collector for customers</label>
            <select
              className="form-input"
              value={formData.defaultRetailCollector}
              onChange={(e) => setField('defaultRetailCollector', e.target.value)}
            >
              <option value="partner">Partner bills the customer</option>
              <option value="platform">Inaiy bills the customer</option>
            </select>
            <span className="form-hint">Retail collector is exclusive per customer. Wholesale can still stack.</span>
          </div>
        </>
      ) : (
        <>
          <div className="form-group">
            <label className="form-label">Who bills this customer</label>
            <select
              className="form-input"
              value={formData.retailCollector}
              onChange={(e) => setField('retailCollector', e.target.value)}
            >
              <option value="">Inherit partner default</option>
              <option value="platform">Inaiy bills the customer</option>
              <option value="partner">Partner bills the customer</option>
            </select>
          </div>
          <label className="form-check">
            <input
              type="checkbox"
              checked={formData.includeInPartnerWholesale}
              onChange={(e) => setField('includeInPartnerWholesale', e.target.checked)}
            />
            Include this customer in partner wholesale usage
          </label>
        </>
      )}
    </>
  );
}

function Tenants() {
  const toast = useToast();
  const confirm = useConfirm();
  const [tenants, setTenants] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [formData, setFormData] = useState(emptyTenantForm);
  const [invite, setInvite] = useState({ email: '', name: '', password: '' });
  const [inviteResult, setInviteResult] = useState(null);
  const [domainVerify, setDomainVerify] = useState(null);
  const [domainVerifyLoading, setDomainVerifyLoading] = useState(false);

  const fetchTenants = async () => {
    try {
      const [allRes, partnersRes] = await Promise.all([
        api.post('/tenant/list', { page: 1, limit: 500 }),
        api.post('/tenant/list', { page: 1, limit: 500, kind: 'partner' }),
      ]);
      setTenants(allRes.data.data?.tenants || []);
      setPartners(partnersRes.data.data?.tenants || []);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      toast.error('Failed to fetch tenants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const handleCreate = () => {
    setEditingTenant(null);
    setFormData(emptyTenantForm);
    setInvite({ email: '', name: '', password: '' });
    setInviteResult(null);
    setShowModal(true);
  };

  const handleEdit = (tenant) => {
    setEditingTenant(tenant);
    setFormData(tenantToForm(tenant));
    setInvite({ email: '', name: '', password: '' });
    setInviteResult(null);
    setDomainVerify(tenant.domainVerification || null);
    setShowModal(true);
  };

  const handleStartDomainVerification = async () => {
    if (!editingTenant) return;
    setDomainVerifyLoading(true);
    try {
      const res = await api.post('/admin/domain-verification/start', { tenantId: getTenantId(editingTenant) });
      setDomainVerify(res.data.data);
      toast.success('Domain verification started. Add DNS records then click Check.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to start verification');
    } finally {
      setDomainVerifyLoading(false);
    }
  };

  const handleCheckDomainVerification = async () => {
    if (!editingTenant) return;
    setDomainVerifyLoading(true);
    try {
      const res = await api.post('/admin/domain-verification/check', { tenantId: getTenantId(editingTenant) });
      setDomainVerify(res.data.data);
      if (res.data.data.status === 'verified') {
        toast.success('Domain verified successfully!');
      } else {
        toast.info('DNS records not yet matching. Check again after propagation.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to check verification');
    } finally {
      setDomainVerifyLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = formToTenantPayload(formData, { includeSubdomain: !editingTenant });
      if (editingTenant) {
        await api.post('/tenant/update', {
          tenantId: getTenantId(editingTenant),
          ...payload,
        });
        toast.success('Tenant updated successfully');
      } else {
        await api.post('/tenant/create', payload);
        toast.success('Tenant created successfully');
      }
      setShowModal(false);
      fetchTenants();
    } catch (error) {
      console.error('Error saving tenant:', error);
      toast.error(error.response?.data?.message || 'Failed to save tenant');
    }
  };

  const handleInvite = async () => {
    if (!editingTenant || formData.kind !== 'partner' || !invite.email) return;
    try {
      const response = await api.post('/admin/partner-admins/create', {
        partnerTenantId: getTenantId(editingTenant),
        email: invite.email,
        name: invite.name || undefined,
        password: invite.password || undefined,
      });
      const temp = response.data?.data?.temporaryPassword;
      setInviteResult(temp || 'Partner admin created');
      toast.success(temp ? `Partner login created. Temporary password: ${temp}` : 'Partner admin created');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create partner admin');
    }
  };

  const handleDelete = async (tenant) => {
    const confirmed = await confirm({
      title: 'Delete tenant',
      message: `Delete tenant "${tenant.name}"? All tenant-product links will be removed.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await api.post('/tenant/delete', { tenantId: getTenantId(tenant) });
      toast.success('Tenant deleted successfully');
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete tenant');
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading tenants..." />;
  }

  return (
    <div className="page">
      <PageHeader
        title="Tenants"
        description="Customer workspaces and reseller partners. Set brand and who bills whom."
        action={
          <button type="button" onClick={handleCreate} className="btn btn--primary">
            <IconPlus width={18} height={18} />
            Create Tenant
          </button>
        }
      />

      <div className="card">
        {tenants.length === 0 ? (
          <EmptyState
            title="No tenants yet"
            description="Create your first tenant to start linking products."
            action={
              <button type="button" onClick={handleCreate} className="btn btn--primary">
                <IconPlus width={18} height={18} />
                Create Tenant
              </button>
            }
          />
        ) : (
          <div className="table-wrapper">
            <table className="data-table data-table--responsive">
              <thead>
                <tr>
                  <th>Subdomain</th>
                  <th>Name</th>
                  <th>Kind</th>
                  <th>Brand</th>
                  <th>Domain</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((tenant) => (
                  <tr key={tenant._id || tenant.subdomain}>
                    <td data-label="Subdomain">{tenant.subdomain}</td>
                    <td data-label="Name">{tenant.name}</td>
                    <td data-label="Kind">
                      <span className={`badge ${tenant.kind === 'partner' ? 'badge--info' : ''}`}>
                        {tenant.kind === 'partner' ? 'Partner' : 'Customer'}
                      </span>
                    </td>
                    <td data-label="Brand">{tenant.brand?.appName || '—'}</td>
                    <td data-label="Domain">{tenant.domain || '—'}</td>
                    <td data-label="Actions" className="cell-actions">
                      <div className="btn-group">
                        <button type="button" onClick={() => handleEdit(tenant)} className="btn btn--secondary btn--sm">
                          Edit
                        </button>
                        <button type="button" onClick={() => handleDelete(tenant)} className="btn btn--danger btn--sm">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <Modal
          title={editingTenant ? 'Edit Tenant' : 'Create Tenant'}
          subtitle={editingTenant ? 'Update identity, brand, and billing' : 'Register a workspace or reseller'}
          onClose={() => setShowModal(false)}
          size="lg"
        >
          <form onSubmit={handleSubmit}>
            <TenantFormFields
              formData={formData}
              setFormData={setFormData}
              editingTenant={editingTenant}
              partners={partners}
            />
            {editingTenant && formData.domain && (
              <>
                <h3 className="form-section-title">Custom Domain Verification</h3>
                {domainVerify?.status === 'verified' ? (
                  <div className="alert alert--success">Domain <strong>{domainVerify.domain || formData.domain}</strong> is verified.</div>
                ) : (
                  <>
                    {domainVerify?.txtName ? (
                      <div className="alert alert--info">
                        <p><strong>Add this TXT record:</strong></p>
                        <p>Name: <code>{domainVerify.txtName}</code></p>
                        <p>Value: <code>{domainVerify.txtValue || domainVerify.token}</code></p>
                        {domainVerify.cnameTarget && (
                          <>
                            <p style={{ marginTop: '0.5rem' }}><strong>Or add CNAME:</strong></p>
                            <p><code>{formData.domain}</code> &rarr; <code>{domainVerify.cnameTarget || domainVerify.expectedCnameTarget}</code></p>
                          </>
                        )}
                        {domainVerify.lastError && <p className="text-muted" style={{ marginTop: '0.5rem' }}>Last error: {domainVerify.lastError}</p>}
                      </div>
                    ) : (
                      <p className="text-muted">Click Start Verification to generate DNS challenge records.</p>
                    )}
                    <div className="btn-group" style={{ marginBottom: '1rem' }}>
                      <button type="button" className="btn btn--secondary btn--sm" disabled={domainVerifyLoading} onClick={handleStartDomainVerification}>
                        {domainVerify?.txtName ? 'Restart Verification' : 'Start Verification'}
                      </button>
                      {domainVerify?.txtName && (
                        <button type="button" className="btn btn--primary btn--sm" disabled={domainVerifyLoading} onClick={handleCheckDomainVerification}>
                          Check DNS
                        </button>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
            {editingTenant && formData.kind === 'partner' && (
              <>
                <h3 className="form-section-title">Partner portal login</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Admin email</label>
                    <input className="form-input" type="email" value={invite.email} onChange={(e) => setInvite((p) => ({ ...p, email: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Name</label>
                    <input className="form-input" value={invite.name} onChange={(e) => setInvite((p) => ({ ...p, name: e.target.value }))} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Password (optional)</label>
                  <input className="form-input" type="text" value={invite.password} onChange={(e) => setInvite((p) => ({ ...p, password: e.target.value }))} placeholder="Leave empty to auto-generate" />
                </div>
                {inviteResult && <div className="alert alert--info">{inviteResult}</div>}
                <button type="button" className="btn btn--secondary" onClick={handleInvite}>
                  Create partner login
                </button>
              </>
            )}
            <div className="modal__footer">
              <button type="button" onClick={() => setShowModal(false)} className="btn btn--secondary">
                Cancel
              </button>
              <button type="submit" className="btn btn--primary">
                {editingTenant ? 'Save Changes' : 'Create Tenant'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export default Tenants;
export { TenantFormFields };
